"use client";

import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useUpdateVehicle } from "@/features/delivery/hooks/use-delivery-actions";
import useUploadFile, { type UploadedFile } from "@/hooks/use-upload-file";
import { VehicleUpdateSchema } from "@/schemas/DeliveryForm/vehicule";
import type { DamagedCarImages, Vehicle } from "@/types/models/vehicle.types";

type RenderProps = {
  getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
  loading: boolean;
  isSuccess: boolean;
  handleImageClick: () => void;
  error?: string;
};

type Props = {
  tooltip: string;
  imagePos: keyof DamagedCarImages;
  url?: string;
  baseImageKey: string;
  viewOnly?: boolean;
  vehicle: Vehicle;
  _key: "imagesBefore" | "imagesAfter";
  extraImagePos?: string;
  render: (props: RenderProps) => React.ReactNode;
};

// WARNING: This component MUST be synchronous (not async) to work with React hooks.
// React 19 does not support async Client Components that use hooks.
function CarImageUpload({
  imagePos,
  url,
  vehicle,
  _key,
  extraImagePos,
  render,
}: Props): React.ReactNode {
  const { contract: id } = useParams();
  const { t } = useTranslation();
  const updateVehicle = useUpdateVehicle();
  const isMutationPending = updateVehicle.isPending;
  const data = updateVehicle.data;

  // Check for required values early
  const vehicleId = vehicle?.id;
  const deliveryId = id;

  if (
    vehicleId === null ||
    vehicleId === undefined ||
    deliveryId === null ||
    deliveryId === undefined
  ) {
    throw new Error("Vehicle or delivery ID is missing");
  }

  const pathExtraImages = `delivery/${deliveryId}/car-${vehicleId}/${_key === "imagesBefore" ? "Before" : "After"}/extra_images/${extraImagePos ?? ""}`;
  const pathImages = `delivery/${deliveryId}/car-${vehicleId}/${_key === "imagesBefore" ? "Before" : "After"}/${imagePos}`;
  const folder = extraImagePos != null ? pathExtraImages : pathImages;

  const subFolder = String(vehicleId);
  const uploadMetadata = {
    module: "delivery",
    subModule: "CarState",
    folder,
    subFolder,
    carId: vehicleId,
    deliveryId,
    imagePos,
    state: _key === "imagesBefore" ? "before" : "after",
    ...(extraImagePos != null ? { extraImagePos } : {}),
  };

  const handleFilesUploaded = useCallback(
    (uploadedFiles: UploadedFile[]) => {
      if (uploadedFiles.length === 0) return;

      const uploadedFile = uploadedFiles[0];
      const newKey = uploadedFile.key;
      const newUrl = uploadedFile.url;

      // Admin uploads have no camera metadata; takenAt/location must be
      // explicitly null to satisfy VehicleImageSchema (extends VehicleImageMetadataSchema).
      const imageValue = { key: newKey, url: newUrl, takenAt: null, location: null };

      // Send a minimal payload (id + changed images object only). Spreading the
      // whole vehicle re-validates unrelated legacy fields (vehicleCode, type,
      // garageImages, sibling image shapes) and blocks the image update.
      const mergedImages = {
        ...(vehicle[_key] ? vehicle[_key] : {}),
        [imagePos]:
          imagePos === "extraImages" && extraImagePos != null
            ? {
                ...(vehicle[_key]?.extraImages ?? {}),
                [extraImagePos]: imageValue,
              }
            : imageValue,
      };

      const updated_vehicle = {
        id: vehicleId,
        [_key]: mergedImages,
      };

      const parsed_data = VehicleUpdateSchema.safeParse(updated_vehicle);
      if (parsed_data.error) {
        console.error("Vehicle image update validation failed", parsed_data.error.format());
        throw new Error("validation error while trying to update car Images");
      }
      updateVehicle.mutate({
        updateInput: parsed_data.data,
      });
    },
    [updateVehicle, vehicle, _key, imagePos, extraImagePos]
  );

  const {
    onDrop: onDropAsync,
    loading: isUploadLoading,
    errorMessage: uploadErrorMessage,
  } = useUploadFile({
    maxFileSize: 15,
    maxFiles: 1,
    uploadedFiles: [],
    metadata: uploadMetadata,
    errorMessages: {
      maxSize: t("validation.imageMaxSize", { size: 15 }),
      fileType: t("validation.imageInvalidType"),
      maxFiles: t("validation.maxFiles", { count: 1 }),
    },
    onFilesUploaded: handleFilesUploaded,
    fileName: imagePos === "extraImages" ? (extraImagePos ?? "") : imagePos,
  });

  // Wrap async onDrop so dropzone can call it without expecting a Promise
  const onDrop = useCallback(
    (
      acceptedFiles: Parameters<typeof onDropAsync>[0],
      fileRejections?: Parameters<typeof onDropAsync>[1],
      event?: Parameters<typeof onDropAsync>[2]
    ) => {
      // Fire and forget - don't await
      const dropEvent = event as Parameters<typeof onDropAsync>[2];
      void onDropAsync(acceptedFiles, fileRejections || [], dropEvent);
    },
    [onDropAsync]
  );

  const handleImageClick = () => {
    if (url != null) {
      window.open(url, "_blank");
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".heic", ".heif"] },
    disabled: isUploadLoading || isMutationPending,
    maxSize: 15 * 1024 * 1024,
    multiple: false,
  });

  const isLoading = isUploadLoading || isMutationPending;
  const combinedError = uploadErrorMessage;

  return render({
    getRootProps,
    getInputProps,
    loading: isLoading,
    isSuccess: Boolean(data?.updateVehicle.id),
    handleImageClick,
    error: combinedError ?? undefined,
  });
}

export default CarImageUpload;
